import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { ArrowLeft, MapPin, Calendar, Tag, ArrowUpRight, CaretLeft, CaretRight } from "@phosphor-icons/react";

export default function ProjectDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(0);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/projects/${id}`);
                setData(data);
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) {
        return <div className="max-w-5xl mx-auto px-6 py-16 text-overline text-neutral-500">Loading…</div>;
    }
    if (!data) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-center">
                <h1 className="font-display text-4xl font-black uppercase mb-3">Project not found</h1>
                <Link to="/discover" className="btn-ghost inline-flex">Browse builders</Link>
            </div>
        );
    }

    const { project, builder } = data;
    const images = project.images || [];
    const current = images[active];

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
            {builder && (
                <Link
                    to={`/b/${builder.slug}`}
                    className="text-overline text-neutral-600 hover:text-black inline-flex items-center gap-2 mb-6"
                    data-testid="back-to-builder"
                >
                    <ArrowLeft size={14} /> Back to {builder.business_name || builder.name}
                </Link>
            )}

            <div className="grid lg:grid-cols-12 gap-10">
                {/* Main image */}
                <div className="lg:col-span-8">
                    <div className="aspect-[4/3] bg-neutral-100 border border-black relative overflow-hidden">
                        {current ? (
                            <img
                                src={fileUrl(current)}
                                alt={project.title}
                                className="w-full h-full object-cover fade-in-up"
                                key={current}
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-neutral-400 uppercase tracking-widest text-xs">
                                No photos
                            </div>
                        )}
                        {images.length > 1 && (
                            <>
                                <button
                                    onClick={() => setActive((active - 1 + images.length) % images.length)}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white border border-black p-2 hover:bg-black hover:text-white"
                                    aria-label="Previous"
                                    data-testid="carousel-prev"
                                >
                                    <CaretLeft size={20} weight="bold" />
                                </button>
                                <button
                                    onClick={() => setActive((active + 1) % images.length)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white border border-black p-2 hover:bg-black hover:text-white"
                                    aria-label="Next"
                                    data-testid="carousel-next"
                                >
                                    <CaretRight size={20} weight="bold" />
                                </button>
                            </>
                        )}
                    </div>
                    {images.length > 1 && (
                        <div className="mt-3 flex gap-2 overflow-x-auto">
                            {images.map((p, i) => (
                                <button
                                    key={p}
                                    onClick={() => setActive(i)}
                                    className={`w-20 h-16 flex-shrink-0 border-2 ${i === active ? "border-[#E63946]" : "border-transparent opacity-60"}`}
                                >
                                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Detail sidebar */}
                <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
                    {project.category && <div className="text-overline text-[#E63946] mb-2">{project.category}</div>}
                    <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-none uppercase mb-5">
                        {project.title}
                    </h1>
                    <div className="space-y-3 text-sm text-neutral-700">
                        {project.location && (
                            <div className="flex items-center gap-2">
                                <MapPin size={16} weight="duotone" /> {project.location}
                            </div>
                        )}
                        {project.completed_date && (
                            <div className="flex items-center gap-2">
                                <Calendar size={16} weight="duotone" /> Completed {project.completed_date}
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <Tag size={16} weight="duotone" /> {images.length} photo{images.length !== 1 ? "s" : ""}
                        </div>
                    </div>

                    {project.description && (
                        <p className="mt-6 text-base text-neutral-700 leading-relaxed whitespace-pre-line">
                            {project.description}
                        </p>
                    )}

                    {builder && (
                        <Link
                            to={`/b/${builder.slug}`}
                            className="mt-8 block border border-black p-5 hover:bg-black hover:text-white transition-colors group"
                            data-testid="builder-card-link"
                        >
                            <div className="text-overline text-[#E63946] group-hover:text-[#E63946] mb-2">Built by</div>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-display text-xl font-bold">
                                        {builder.business_name || builder.name}
                                    </div>
                                    {builder.location && (
                                        <div className="text-xs uppercase tracking-widest opacity-70">
                                            {builder.location}
                                        </div>
                                    )}
                                </div>
                                <ArrowUpRight size={22} weight="bold" className="group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}
